import React, { useState } from 'react';
import {
  ArrowTopRightOnSquareIcon,
  EnvelopeIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { useQuery, useInfiniteQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import clsx from 'clsx';

import useChargeQueries from '@dashboard/hooks/queries/useChargeQueries';
import useSubscriptionQueries from '@dashboard/hooks/queries/useSubscriptionQueries';
import { useCustomerQueries } from '@dashboard/hooks/queries/useCustomerQueries';
import useSubmissionQueries from '@dashboard/hooks/queries/useSubmissionQueries';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import { customerKey } from '@dashboard/lib/queryKeys';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';
import { useToast } from '@dashboard/context/ToastContext';

import { fetcher } from '@dashboard/services/request';
import { countries } from '@dashboard/services/countries/countries';

import Link from '@dashboard/components/Link';
import Button from '@dashboard/components/Button';
import Card from '@dashboard/components/Card';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import CardContent from '@dashboard/components/CardContent';
import { Box, BoxItem, BoxTitle, BoxContent } from '@dashboard/components/Box';
import Spinner from '@dashboard/components/Spinner';
import CardListItem from '@dashboard/components/CardListItem';
import CardListItemElement from '@dashboard/components/CardListItemElement';
import Payment from '@dashboard/components/PaymentItem';
import SubscriptionItem from '@dashboard/components/SubscriptionItem';
import SubmissionListItem from '@dashboard/components/SubmissionListItem';
import PropertyList from '@dashboard/components/PropertyList';
import Form from '@dashboard/components/Formik/Form';
import Dialog from '@dashboard/components/Dialog';
import { useAdminGhosting } from '@dashboard/context/AdminGhostingContext/AdminGhostingContext';

export default function CustomerDetail({ match }) {
  const { seller, isAdminLoggedInAsSeller } = useAuth();
  const { setAdminGhostingSellerId } = useAdminGhosting();
  const { getCustomerById } = useCustomerQueries();

  const { data: customer, error } = useQuery({
    queryKey: customerKey(match.params.id),
    queryFn: () => getCustomerById(match.params.id),
  });

  React.useEffect(() => {
    if (customer?.seller) {
      setAdminGhostingSellerId(customer.seller);
    }
  }, [customer?.seller, setAdminGhostingSellerId]);

  if (!customer && !error) {
    return (
      <Card variant="root">
        <CardContent variant="root">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card variant="root">
        <CardContent variant="root">Something went wrong</CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card variant="root">
        <CardHeader variant="root">
          <div>
            <CardHeaderTitle variant="root">{customer.name || 'Name not provided'}</CardHeaderTitle>

            {customer.email && (
              <Link
                href={`mailto:${customer.email}`}
                target="_blank"
                rel="noreferrer noopener"
                variant="withIcon"
              >
                {customer.email} <EnvelopeIcon className="h-4 w-4 mt-0.5" />
              </Link>
            )}
          </div>
        </CardHeader>

        <CardContent variant="root" borderBottom>
          <Box>
            <Box style={{ padding: 0 }}>
              <BoxItem>
                <BoxTitle>Since</BoxTitle>
                <BoxContent>
                  {format(new Date(customer.createdAt), 'd MMM, yyyy, HH:mm')}
                </BoxContent>
              </BoxItem>

              {customer.stripeCustomerId && (
                <BoxItem>
                  <BoxTitle>Stripe customer</BoxTitle>
                  <BoxContent>
                    <Link
                      to={getStripeDashboardUrl(`/customers/${customer.stripeCustomerId}`, {
                        seller,
                        isAdminLoggedInAsSeller,
                      })}
                      blank
                      className={clsx(
                        `border-b-2 border-gray-100 w-full flex items-center gap-x-2`,
                      )}
                    >
                      <ArrowTopRightOnSquareIcon className="w-4 h-4" /> View in Stripe
                    </Link>
                  </BoxContent>
                </BoxItem>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card className="max-w-2xl">
        <CardContent>
          <PropertyList.Title>
            Customer details
            <div className="mt-auto ml-auto">
              <CustomerEditDialog customer={customer} />
            </div>
          </PropertyList.Title>

          <PropertyList.Item>
            <PropertyList.ItemLabel>Name</PropertyList.ItemLabel>
            <PropertyList.ItemValue>{customer.name}</PropertyList.ItemValue>
          </PropertyList.Item>

          <PropertyList.Item>
            <PropertyList.ItemLabel>Company name</PropertyList.ItemLabel>
            <PropertyList.ItemValue>{customer.companyName}</PropertyList.ItemValue>
          </PropertyList.Item>

          <PropertyList.Item>
            <PropertyList.ItemLabel>Email</PropertyList.ItemLabel>
            <PropertyList.ItemValue>{customer.email}</PropertyList.ItemValue>
          </PropertyList.Item>

          <PropertyList.Item>
            <PropertyList.ItemLabel>Billing email</PropertyList.ItemLabel>
            <PropertyList.ItemValue>{customer.billingEmail}</PropertyList.ItemValue>
          </PropertyList.Item>

          <PropertyList.Item>
            <PropertyList.ItemLabel>Phone</PropertyList.ItemLabel>
            <PropertyList.ItemValue>{customer.phone}</PropertyList.ItemValue>
          </PropertyList.Item>

          <PropertyList.Item>
            <PropertyList.ItemLabel>Billing address</PropertyList.ItemLabel>
            <PropertyList.ItemValue>
              {customer.address && (
                <div>
                  <div>{customer.address.line1}</div>
                  <div>{customer.address.line2}</div>
                  <div>{customer.address.city}</div>
                  <div>{customer.address.state}</div>
                  <div>{customer.address.postalCode}</div>
                  <div>{customer.address.country}</div>
                </div>
              )}
            </PropertyList.ItemValue>
          </PropertyList.Item>

          <PropertyList.Item>
            <PropertyList.ItemLabel>Shipping address</PropertyList.ItemLabel>
            <PropertyList.ItemValue>
              {customer.shipping && (
                <div>
                  <div>{customer.shipping.name}</div>
                  <div>{customer.shipping.phone}</div>
                  {customer.shipping.address && (
                    <div>
                      <div>{customer.shipping.address.line1}</div>
                      <div>{customer.shipping.address.line2}</div>
                      <div>{customer.shipping.address.city}</div>
                      <div>{customer.shipping.address.state}</div>
                      <div>{customer.shipping.address.postalCode}</div>
                      <div>{customer.shipping.address.country}</div>
                    </div>
                  )}
                </div>
              )}
            </PropertyList.ItemValue>
          </PropertyList.Item>
        </CardContent>
      </Card>

      <CustomerCharges customer={customer} />

      <CustomerSubscriptions customer={customer} />

      <CustomerBookings customer={customer} />

      <CustomerSubmissions customer={customer} />
    </>
  );
}

function CustomerCharges({ customer }) {
  const { getInfiniteCharges } = useChargeQueries();

  const { data, fetchNextPage, isFetchingNextPage, isError, isLoading, hasNextPage } =
    useInfiniteQuery({
      queryKey: ['charges'],
      queryFn: async ({ pageParam }) =>
        await getInfiniteCharges(customer.seller, {
          customer: customer._id,
          limit: 5,
          skip: pageParam * 5,
          livemode: true,
          pageType: 'checkout',
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage.has_more) return undefined;

        return allPages.length;
      },
    });

  if (isLoading) {
    return (
      <Card variant="root">
        <CardHeader variant="root">
          <CardHeaderTitle variant="root">One-time payments</CardHeaderTitle>
        </CardHeader>
        <CardContent variant="root">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card variant="root">
        <CardHeader variant="root">
          <CardHeaderTitle variant="root">One-time payments</CardHeaderTitle>
        </CardHeader>
        <CardContent variant="card">
          Unable to load payments. Please try again or contact support.
        </CardContent>
      </Card>
    );
  }

  if (!data?.pages?.[0]?.data?.length) {
    return (
      <Card variant="root">
        <CardHeader variant="root">
          <CardHeaderTitle variant="root">One-time payments</CardHeaderTitle>
        </CardHeader>

        <CardContent className="text-gray-500" variant="root">
          No payments
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="root" borderBottom>
      <CardHeader variant="root">
        <CardHeaderTitle variant="root">One-time payments</CardHeaderTitle>
      </CardHeader>
      <CardContent className="overflow-auto">
        <CardListItem variant="header">
          <CardListItemElement size="large">Amount</CardListItemElement>
          <CardListItemElement size="4xl">Page</CardListItemElement>
          <CardListItemElement size="xl" align="right">
            Date
          </CardListItemElement>
          <CardListItemElement size="large">Abandonment</CardListItemElement>
          <CardListItemElement size="medium">Status</CardListItemElement>
        </CardListItem>

        <div>
          {data?.pages?.map(data =>
            data?.data
              .filter(charge => !charge.tickets || charge.tickets.length === 0)
              .map(charge => (
                <Payment payment={charge} key={charge._id} options={{ hideCustomer: true }} />
              )),
          )}
        </div>

        <div className="py-4 px-4">
          {!isFetchingNextPage && hasNextPage && (
            <Button onClick={fetchNextPage} appearance="light">
              Load more
            </Button>
          )}

          {isFetchingNextPage && <Spinner className="mx-4" />}
        </div>
      </CardContent>
    </Card>
  );
}

function CustomerBookings({ customer }) {
  const { getInfiniteCharges } = useChargeQueries();

  const { data, fetchNextPage, isFetchingNextPage, isError, isLoading, hasNextPage } =
    useInfiniteQuery({
      queryKey: ['charges', 'bookings'],
      queryFn: async ({ pageParam }) =>
        await getInfiniteCharges(customer.seller, {
          customer: customer._id,
          limit: 5,
          skip: pageParam * 5,
          livemode: true,
          pageType: 'event',
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage.has_more) return undefined;

        return allPages.length;
      },
    });

  if (isLoading) {
    return (
      <Card variant="root">
        <CardHeader variant="root">
          <CardHeaderTitle variant="root">Event bookings</CardHeaderTitle>
        </CardHeader>
        <CardContent variant="root">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card variant="root">
        <CardHeader variant="root">
          <CardHeaderTitle variant="root">Event bookings</CardHeaderTitle>
        </CardHeader>
        <CardContent variant="card">
          Unable to load event bookings. Please try again or contact support.
        </CardContent>
      </Card>
    );
  }

  if (!data?.pages?.[0]?.data?.length) {
    return (
      <Card variant="root">
        <CardHeader variant="root">
          <CardHeaderTitle variant="root">Event bookings</CardHeaderTitle>
        </CardHeader>

        <CardContent className="text-gray-500" variant="root">
          No event bookings
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="root" borderBottom>
      <CardHeader variant="root">
        <CardHeaderTitle variant="root">Event bookings</CardHeaderTitle>
      </CardHeader>
      <CardContent className="overflow-auto">
        <CardListItem variant="header">
          <CardListItemElement size="large">Amount</CardListItemElement>
          <CardListItemElement size="4xl">Page</CardListItemElement>
          <CardListItemElement size="xl" align="right">
            Date
          </CardListItemElement>
          <CardListItemElement size="large">Abandonment</CardListItemElement>
          <CardListItemElement size="medium">Status</CardListItemElement>
        </CardListItem>

        <div>
          {data?.pages?.map(data =>
            data?.data
              .filter(charge => charge.tickets && charge.tickets.length > 0)
              .map(charge => (
                <Payment payment={charge} key={charge._id} options={{ hideCustomer: true }} />
              )),
          )}
        </div>

        <div className="py-4 px-4">
          {!isFetchingNextPage && hasNextPage && (
            <Button onClick={fetchNextPage} appearance="light">
              Load more
            </Button>
          )}

          {isFetchingNextPage && <Spinner className="mx-4" />}
        </div>
      </CardContent>
    </Card>
  );
}

function CustomerSubscriptions({ customer }) {
  const { getInfiniteSubscriptions } = useSubscriptionQueries();

  const { data, fetchNextPage, isFetchingNextPage, isError, isLoading, hasNextPage } =
    useInfiniteQuery({
      queryKey: ['subscriptions'],
      queryFn: async ({ pageParam }) =>
        await getInfiniteSubscriptions(customer.seller, {
          customer: customer._id,
          limit: 5,
          skip: pageParam * 5,
          livemode: true,
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage.has_more) return undefined;

        return allPages.length;
      },
    });

  if (isLoading) {
    return (
      <Card variant="root">
        <CardHeader variant="root">
          <CardHeaderTitle variant="root">Subscriptions</CardHeaderTitle>
        </CardHeader>
        <CardContent variant="root">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card variant="root">
        <CardHeader variant="root">
          <CardHeaderTitle variant="root">Subscriptions</CardHeaderTitle>
        </CardHeader>
        <CardContent variant="card">
          Unable to load subscriptions. Please try again or contact support.
        </CardContent>
      </Card>
    );
  }

  if (!data?.pages?.[0]?.data?.length) {
    return (
      <Card variant="root">
        <CardHeader variant="root">
          <CardHeaderTitle variant="root">Subscriptions</CardHeaderTitle>
        </CardHeader>

        <CardContent className="text-gray-500" variant="root">
          No subscriptions
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="root" borderBottom>
      <CardHeader variant="root">
        <CardHeaderTitle variant="root">Subscriptions</CardHeaderTitle>
      </CardHeader>
      <CardContent style={{ padding: 0 }}>
        <CardListItem variant="header">
          <CardListItemElement size="xl">Amount</CardListItemElement>
          <CardListItemElement size="4xl">Page</CardListItemElement>
          <CardListItemElement size="xl" align="right">
            Date
          </CardListItemElement>
          <CardListItemElement size="large">Abandonment</CardListItemElement>
          <CardListItemElement size="medium">Status</CardListItemElement>
        </CardListItem>

        <div className="overflow-auto">
          {data?.pages?.map(data =>
            data?.data.map(subscription => (
              <SubscriptionItem
                subscription={subscription}
                key={subscription._id}
                options={{ hideCustomer: true }}
              />
            )),
          )}
        </div>

        <div className="py-2 px-4">
          {!isFetchingNextPage && hasNextPage && (
            <Button onClick={fetchNextPage} appearance="light">
              Load more
            </Button>
          )}

          {isFetchingNextPage && <Spinner className="mx-4" />}
        </div>
      </CardContent>
    </Card>
  );
}

function CustomerSubmissions({ customer }) {
  const { getInfiniteSubmissions } = useSubmissionQueries();

  const { data, fetchNextPage, isFetchingNextPage, isError, isLoading, hasNextPage } =
    useInfiniteQuery({
      queryKey: ['submissions'],
      queryFn: async ({ pageParam }) =>
        await getInfiniteSubmissions(customer.seller, {
          customer: customer._id,
          limit: 5,
          skip: pageParam * 5,
          livemode: true,
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage.has_more) return undefined;

        return allPages.length;
      },
    });

  if (isLoading) {
    return (
      <Card variant="root">
        <CardHeader variant="root">
          <CardHeaderTitle variant="root">Submissions</CardHeaderTitle>
        </CardHeader>
        <CardContent variant="root">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card variant="root">
        <CardHeader variant="root">
          <CardHeaderTitle variant="root">Submissions</CardHeaderTitle>
        </CardHeader>
        <CardContent variant="card">
          Unable to load submissions. Please try again or contact support.
        </CardContent>
      </Card>
    );
  }

  if (!data?.pages?.[0]?.data?.length) {
    return (
      <Card variant="root">
        <CardHeader variant="root">
          <CardHeaderTitle variant="root">Submissions</CardHeaderTitle>
        </CardHeader>

        <CardContent className="text-gray-500" variant="root">
          No submissions
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="root" borderBottom>
      <CardHeader variant="root">
        <CardHeaderTitle variant="root">Submissions</CardHeaderTitle>
      </CardHeader>
      <CardContent style={{ padding: 0 }}>
        <CardListItem variant="header">
          <CardListItemElement size="3xl">Customer</CardListItemElement>
          <CardListItemElement size="3xl">Form</CardListItemElement>
          <CardListItemElement size="xl" align="right">
            Date
          </CardListItemElement>
          <CardListItemElement size="medium">Status</CardListItemElement>
        </CardListItem>

        <div className="overflow-auto">
          {data?.pages?.map(data =>
            data?.data.map(submission => (
              <SubmissionListItem key={submission._id} submission={submission} />
            )),
          )}
        </div>

        <div className="py-2 px-4">
          {!isFetchingNextPage && hasNextPage && (
            <Button onClick={fetchNextPage} appearance="light">
              Load more
            </Button>
          )}

          {isFetchingNextPage && <Spinner className="mx-4" />}
        </div>
      </CardContent>
    </Card>
  );
}

function CustomerEditDialog({ customer }) {
  const [isOpen, setIsOpen] = useState(false);
  const { seller, token } = useAuth();
  const queryClient = useQueryClient();
  const { emitToast } = useToast();

  const { mutateAsync: updateCustomer } = useMutation({
    mutationFn: async values => {
      return await fetcher(`/sellers/${seller._id}/customers/${customer._id}`, token, {
        method: 'PUT',
        body: JSON.stringify(values),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerKey(customer._id) });

      setIsOpen(false);

      emitToast({
        title: 'Customer details updated',
      });
    },
  });

  return (
    <Dialog
      trigger={
        <Button
          appearance="light"
          size="small"
          className="ml-2 flex items-center gap-x-2"
          onClick={() => setIsOpen(true)}
        >
          <PencilSquareIcon className="w-4 h-4" /> Edit
        </Button>
      }
      title="Edit customer details"
      open={isOpen}
      onOpenChange={setIsOpen}
      onCancel={() => setIsOpen(false)}
      confirmButton={null}
      cancelButton={null}
    >
      {({ close }) => {
        const fields = getCustomerEditFields(customer);

        return (
          <Form
            fields={fields}
            initialValues={{
              name: customer.name,
              companyName: customer.companyName,
              email: customer.email,
              billingEmail: customer.billingEmail,
              phone: customer.phone,
              ...(customer.address
                ? {
                    address: {
                      line1: customer.address.line1,
                      line2: customer.address.line2,
                      city: customer.address.city,
                      state: customer.address.state,
                      postalCode: customer.address.postalCode,
                      country: customer.address.country,
                    },
                  }
                : {}),
              ...(customer.shipping
                ? {
                    shipping: {
                      name: customer.shipping.name,
                      phone: customer.shipping.phone,
                      ...(customer.shipping.address
                        ? {
                            address: {
                              line1: customer.shipping.address.line1,
                              line2: customer.shipping.address.line2,
                              city: customer.shipping.address.city,
                              state: customer.shipping.address.state,
                              postalCode: customer.shipping.address.postalCode,
                              country: customer.shipping.address.country,
                            },
                          }
                        : {}),
                    },
                  }
                : {}),
            }}
            onSubmit={async (values, actions) => {
              try {
                const data = await updateCustomer(values);

                actions.setStatus('');
                actions.resetForm({
                  values: {
                    name: data.name,
                    companyName: data.companyName,
                    email: data.email,
                    billingEmail: data.billingEmail,
                    phone: data.phone,
                    ...(data.address
                      ? {
                          address: {
                            line1: data.address.line1,
                            line2: data.address.line2,
                            city: data.address.city,
                            state: data.address.state,
                            postalCode: data.address.postalCode,
                            country: data.address.country,
                          },
                        }
                      : {}),
                    ...(data.shipping
                      ? {
                          shipping: {
                            name: data.shipping.name,
                            phone: data.shipping.phone,
                            ...(data.shipping.address
                              ? {
                                  address: {
                                    line1: data.shipping.address.line1,
                                    line2: data.shipping.address.line2,
                                    city: data.shipping.address.city,
                                    state: data.shipping.address.state,
                                    postalCode: data.shipping.address.postalCode,
                                    country: data.shipping.address.country,
                                  },
                                }
                              : {}),
                          },
                        }
                      : {}),
                  },
                });

                return {
                  data: true,
                };
              } catch (error) {
                return {
                  error,
                };
              }
            }}
            cancelButton={{
              label: 'Cancel',
              appearance: 'light',
              onClick: close,
            }}
            formSubmitAlign="end"
          />
        );
      }}
    </Dialog>
  );
}

function getCustomerEditFields(customer) {
  return [
    {
      type: 'text',
      name: 'name',
      label: 'Name',
      _value: customer.name || '',
    },
    {
      type: 'text',
      name: 'companyName',
      label: 'Company name',
      _value: customer.companyName || '',
    },
    {
      type: 'email',
      name: 'email',
      label: 'Account email',
      _value: customer.email || '',
    },
    {
      type: 'email',
      name: 'billingEmail',
      label: 'Billing email',
      _value: customer.billingEmail || '',
    },
    {
      type: 'tel',
      name: 'phone',
      label: 'Phone',
      _value: customer.phone || '',
    },
    {
      type: 'fieldgroup',
      name: 'address',
      label: 'Billing address',
      fields: [
        {
          type: 'text',
          name: 'address.line1',
          placeholder: 'Address line 1',
          _value: customer.address?.line1 || '',
        },
        {
          type: 'text',
          name: 'address.line2',
          placeholder: 'Address line 2',
          _value: customer.address?.line2 || '',
        },
        {
          type: 'text',
          name: 'address.city',
          placeholder: 'City',
          _value: customer.address?.city || '',
        },
        {
          type: 'text',
          name: 'address.state',
          placeholder: 'State',
          _value: customer.address?.state || '',
        },
        {
          type: 'text',
          name: 'address.postalCode',
          placeholder: 'Postal code',
          _value: customer.address?.postalCode || '',
        },
        {
          type: 'select',
          name: 'address.country',
          placeholder: 'Country',
          _value: customer.address?.country || '',
          options: [
            {
              label: 'Select country',
              value: '',
            },
            ...countries.map(country => ({
              label: country.name,
              value: country.alpha2,
            })),
          ],
        },
      ],
    },
    {
      type: 'fieldgroup',
      name: 'shipping',
      label: 'Shipping address',
      fields: [
        {
          type: 'text',
          name: 'shipping.name',
          placeholder: 'Name',
          _value: customer.shipping?.name || '',
        },
        {
          type: 'tel',
          name: 'shipping.phone',
          placeholder: 'Phone',
          _value: customer.shipping?.phone || '',
        },
        {
          type: 'text',
          name: 'shipping.address.line1',
          placeholder: 'Address line 1',
          _value: customer.shipping?.address?.line1 || '',
        },
        {
          type: 'text',
          name: 'shipping.address.line2',
          placeholder: 'Address line 2',
          _value: customer.shipping?.address?.line2 || '',
        },
        {
          type: 'text',
          name: 'shipping.address.city',
          placeholder: 'City',
          _value: customer.shipping?.address?.city || '',
        },
        {
          type: 'text',
          name: 'shipping.address.state',
          placeholder: 'State',
          _value: customer.shipping?.address?.state || '',
        },
        {
          type: 'text',
          name: 'shipping.address.postalCode',
          placeholder: 'Postal code',
          _value: customer.shipping?.address?.postalCode || '',
        },
        {
          type: 'select',
          name: 'shipping.address.country',
          placeholder: 'Country',
          _value: customer.shipping?.address?.country || '',
          options: [
            {
              label: 'Select country',
              value: '',
            },
            ...countries.map(country => ({
              label: country.name,
              value: country.alpha2,
            })),
          ],
        },
      ],
    },
  ];
}
